/**
 * persona-from-title.ts
 *
 * Pure helper. Given a contact title string, infer which template-library
 * Persona the rep should be shown — so a CFO call surfaces finance
 * scripts, not HR or operations scripts.
 *
 * Order of evaluation matters: finance is checked first because controllers
 * and treasurers often carry "ops" or "people" adjacent language; HR is
 * checked before operations because CHRO/CPO titles can contain the word
 * "operations". If nothing matches we return null and the caller decides
 * the default (typically `operations`, the most common manufacturing
 * title pool in Michael's book).
 */

import type { Persona } from "./template-library";

const FINANCE_KEYWORDS = [
  "cfo",
  "chief financial",
  "controller",
  "treasurer",
  "treasury",
  "vp finance",
  "vp of finance",
  "finance director",
  "director of finance",
  "head of finance",
  "finance manager",
  "fp&a",
  "fpa",
  "accounting",
];

const HR_KEYWORDS = [
  "chro",
  "chief people",
  "cpo",
  "vp hr",
  "vp of hr",
  "vp human",
  "vp of human",
  "human resources",
  "people operations",
  "people ops",
  "talent",
  "hr director",
  "director of hr",
  "head of hr",
  "head of people",
  "hr manager",
  "hr business partner",
];

const OPERATIONS_KEYWORDS = [
  "coo",
  "chief operating",
  "operations",
  "plant manager",
  "plant director",
  "vp operations",
  "vp of operations",
  "head of operations",
  "operations director",
  "director of operations",
  "operations manager",
  "manufacturing",
  "production",
  "supply chain",
  "continuous improvement",
  "lean",
  "process improvement",
  "site leader",
  "plant",
];

/**
 * Infer a Persona from a free-form title. Returns null if nothing matches.
 * Case-insensitive substring match.
 */
export function inferPersonaFromTitle(title?: string | null): Persona | null {
  if (!title || typeof title !== "string") return null;
  const t = title.toLowerCase();

  // Finance first — strongest signal for our target buyers.
  if (FINANCE_KEYWORDS.some((k) => t.includes(k))) return "finance";

  // HR before operations — "people operations" should not route to operations.
  if (HR_KEYWORDS.some((k) => t.includes(k))) return "hr";

  // Operations last among the three.
  if (OPERATIONS_KEYWORDS.some((k) => t.includes(k))) return "operations";

  return null;
}

/**
 * Human-readable persona label, matching the template-library browser.
 */
export const PERSONA_LABEL: Record<Persona, string> = {
  finance: "Finance",
  operations: "Operations",
  hr: "HR",
};

/**
 * Convenience: infer with a default fallback so callers always get a value.
 * Default is `operations` because Michael's book skews manufacturing
 * plant-level. Override per-call if a different default is more appropriate.
 */
export function inferPersonaWithDefault(
  title?: string | null,
  fallback: Persona = "operations"
): Persona {
  return inferPersonaFromTitle(title) ?? fallback;
}
